/**
 * Real Patient Identity API calls (STEP 1 audit, `PATIENT-UI-BACKEND-MAPPING.md`
 * §1) — every function here maps 1:1 to a real, mounted route on
 * `patientRouter`. `getPatientById` closes Gap A (`GET /:patientId` now
 * exists); no update endpoint exists yet — see the mapping doc's Gap B.
 */
import { httpClient, unwrap, API_ENDPOINTS } from '@core/api';
import type { ApiSuccessResponse, PaginatedResult } from '@app-types/api.types';
import type {
  RegisterPatientRequest,
  RegisterEmergencyRequest,
  RegisterUnknownRequest,
  RegisterNewbornRequest,
  RegisterCorporateRequest,
  RegisterReferralRequest,
  RegisterInsuranceRequest,
  SearchPatientRequest,
  AdvancedSearchRequest,
  PatientDto,
  PatientAddressDto,
  PatientContactDto,
  TimelineEntryDto,
} from '@nexao/contracts';

const BASE = API_ENDPOINTS.patients;

export async function registerStandard(request: RegisterPatientRequest): Promise<PatientDto> {
  return unwrap(httpClient.post<ApiSuccessResponse<PatientDto>>(BASE, request));
}

export async function registerEmergency(request: RegisterEmergencyRequest): Promise<PatientDto> {
  return unwrap(httpClient.post<ApiSuccessResponse<PatientDto>>(`${BASE}/emergency`, request));
}

export async function registerUnknown(request: RegisterUnknownRequest): Promise<PatientDto> {
  return unwrap(httpClient.post<ApiSuccessResponse<PatientDto>>(`${BASE}/unknown`, request));
}

export async function registerNewborn(request: RegisterNewbornRequest): Promise<PatientDto> {
  return unwrap(httpClient.post<ApiSuccessResponse<PatientDto>>(`${BASE}/newborn`, request));
}

export async function registerCorporate(request: RegisterCorporateRequest): Promise<PatientDto> {
  return unwrap(httpClient.post<ApiSuccessResponse<PatientDto>>(`${BASE}/corporate`, request));
}

export async function registerReferral(request: RegisterReferralRequest): Promise<PatientDto> {
  return unwrap(httpClient.post<ApiSuccessResponse<PatientDto>>(`${BASE}/referral`, request));
}

/** `POST /patients/insurance` — real, mounted, permission-gated route (confirmed directly in the deployed `patient.routes.js`) with no prior frontend caller. */
export async function registerInsurance(request: RegisterInsuranceRequest): Promise<PatientDto> {
  return unwrap(httpClient.post<ApiSuccessResponse<PatientDto>>(`${BASE}/insurance`, request));
}

export async function searchPatients(request: SearchPatientRequest): Promise<PaginatedResult<PatientDto>> {
  return unwrap(httpClient.post<ApiSuccessResponse<PaginatedResult<PatientDto>>>(`${BASE}/search`, request));
}

export async function advancedSearchPatients(request: AdvancedSearchRequest): Promise<PatientDto[]> {
  return unwrap(httpClient.post<ApiSuccessResponse<PatientDto[]>>(`${BASE}/advanced-search`, request));
}

export async function getPatientTimeline(patientId: string, limit?: number): Promise<TimelineEntryDto[]> {
  return unwrap(
    httpClient.get<ApiSuccessResponse<TimelineEntryDto[]>>(`${BASE}/${patientId}/timeline`, { params: limit ? { limit } : undefined }),
  );
}

export async function getPatientById(patientId: string): Promise<PatientDto> {
  return unwrap(httpClient.get<ApiSuccessResponse<PatientDto>>(`${BASE}/${patientId}`));
}

/** Real feedback — "address data also required": PatientDto never returns it, this closes that gap. */
export async function getPatientAddresses(patientId: string): Promise<PatientAddressDto[]> {
  return unwrap(httpClient.get<ApiSuccessResponse<PatientAddressDto[]>>(`${BASE}/${patientId}/addresses`));
}

/** Real feedback — the UHID/Health cards must print the mobile number; PatientDto never returns it either. */
export async function getPatientContacts(patientId: string): Promise<PatientContactDto[]> {
  return unwrap(httpClient.get<ApiSuccessResponse<PatientContactDto[]>>(`${BASE}/${patientId}/contacts`));
}

// There is NO direct save of a patient's details any more (retired 2026-10-07): a change is REQUESTED through
// `features/billing/services/patient-correction.service.ts` and applied only when a verifier and an approver have decided it.
